/**
 * AskTheCountyUI  (branded "On the Record")
 *
 * Two-column layout:
 *   LEFT  — ChatToolUI (full conversation)
 *   RIGHT — WorkspaceContextPanel (Claude-style artifact panel with workspace info)
 *
 * Workspace context is loaded from the workspaceId carried in router location
 * state whenever the user navigates here from a Workspace detail page.
 */
import React, { useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { FileText, Calendar } from 'lucide-react';
import { ChatToolUI } from './ChatToolUI';
import { useWorkspaceDetail } from '@/hooks/useWorkspaceDetail';
import { useWorkspacePanel } from '@/hooks/useWorkspacePanel';
import { useWorkspaces } from '@/hooks/useWorkspaces';
import type { Tool } from '@/types/database';

interface Props {
  tool: Tool;
}

interface LocationState {
  preselectedWorkspaceId?: string;
  prefillQuestion?: string;
}

// ── Workspace Context Panel ───────────────────────────────────────────────────

interface PanelProps {
  workspaceId: string;
  onAsk: (q: string) => void;
  /** Live follow-up questions from the most recent LLM response */
  followUpQuestions: string[];
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatMonthYear(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric',
  });
}

const WorkspaceContextPanel: React.FC<PanelProps> = ({
  workspaceId,
  onAsk,
  followUpQuestions,
}) => {
  // Primary source: lightweight /panel payload (workspace + coverage +
  // suggested questions) — renders without waiting on the heavier /detail
  // payload (corpus stats, tool_runs).
  const {
    data: panelData,
    loading: panelLoading,
    error: panelError,
  } = useWorkspacePanel(workspaceId);
  // If the lightweight endpoint fails, fall back to the /detail payload so the
  // panel still renders (no regression on older servers).
  const { data: detailData, loading: detailLoading } =
    useWorkspaceDetail(workspaceId);
  const useFallback = panelError != null;

  const workspace = useFallback
    ? (detailData?.workspace ?? null)
    : (panelData?.workspace ?? null);
  const stats = useFallback
    ? (detailData?.quick_stats ?? null)
    : (panelData?.quick_stats ?? null);
  const headerLoading = useFallback ? detailLoading : panelLoading;

  // Suggested questions come from the panel payload (static templates on new
  // conversations, live LLM follow-ups after answers).
  const questions =
    followUpQuestions.length > 0
      ? followUpQuestions
      : useFallback
        ? (detailData?.suggested_questions ?? [])
        : (panelData?.suggested_questions ?? []);
  const sectionLabel =
    followUpQuestions.length > 0
      ? 'Follow-up Questions'
      : 'Suggested Questions';
  const questionsLoading = useFallback ? detailLoading : panelLoading;

  return (
    <div className="flex flex-col gap-3 h-full overflow-y-auto">
      {/* ── Header card ──────────────────────────────────────────────── */}
      <div className="rounded-xl border border-gray-300 bg-white p-4">
        <p className="mb-1 text-[10px] font-bold uppercase tracking-wide text-gray-900">
          Active Workspace
        </p>
        {headerLoading ? (
          <div className="space-y-2 animate-pulse">
            <div className="h-4 w-3/4 rounded bg-gray-200" />
            <div className="h-3 w-1/2 rounded bg-gray-100" />
          </div>
        ) : (
          <>
            <p className="text-sm font-bold text-gray-900 leading-snug">
              {workspace?.name ?? '—'}
            </p>
            {workspace?.description && (
              <p className="mt-1 text-xs text-gray-900 leading-snug">
                {workspace.description}
              </p>
            )}
          </>
        )}
      </div>

      {/* ── Coverage period ───────────────────────────────────────────── */}
      {!headerLoading && stats?.coverage_from && (
        <div className="rounded-xl border border-gray-300 bg-white p-4">
          <p className="mb-1 text-[10px] font-bold uppercase tracking-wide text-gray-900 flex items-center gap-1">
            <Calendar className="h-3 w-3" /> Coverage Period
          </p>
          <p className="text-xs font-medium text-gray-700">
            {formatMonthYear(stats.coverage_from)} —{' '}
            {stats.coverage_to ? formatDate(stats.coverage_to) : 'present'}
          </p>
          <p className="text-[10px] text-gray-500">
            (date range of {stats.dated_docs.toLocaleString()} dated document
            {stats.dated_docs !== 1 ? 's' : ''})
          </p>
          {stats.total_docs != null && (
            <p className="mt-1 text-[11px] text-gray-900">
              {stats.total_docs.toLocaleString()} total document
              {stats.total_docs !== 1 ? 's' : ''}
              {stats.dated_docs < stats.total_docs && (
                <span className="text-gray-500">
                  {' '}
                  ({stats.dated_docs.toLocaleString()} with a known meeting
                  date)
                </span>
              )}
            </p>
          )}
        </div>
      )}

      {/* ── Questions panel ───────────────────────────────────────────── */}
      {(questionsLoading || questions.length > 0) && (
        <div className="rounded-xl border border-gray-300 bg-white p-4 flex flex-col gap-2 flex-1 min-h-0">
          <p className="text-[10px] font-bold uppercase tracking-wide text-gray-900 flex items-center gap-1">
            <FileText className="h-3 w-3" /> {sectionLabel}
          </p>

          {questionsLoading ? (
            <div className="space-y-2 animate-pulse">
              <div className="h-3 w-2/3 rounded bg-gray-200" />
              <div className="h-3 w-1/2 rounded bg-gray-100" />
              <div className="h-3 w-3/4 rounded bg-gray-100" />
            </div>
          ) : (
            <ul className="space-y-0.5 overflow-y-auto flex-1">
              {questions.map((q, i) => (
                <li key={i}>
                  <button
                    type="button"
                    onClick={() => onAsk(q)}
                    className="w-full text-left flex items-start gap-1.5 rounded-lg px-2 py-1.5
                               text-xs text-gray-800 hover:bg-blue-50 hover:text-gray-900
                               transition-colors group"
                  >
                    <span className="text-gray-300 group-hover:text-blue-400 flex-shrink-0 transition-colors mt-px">
                      ✦
                    </span>
                    <span className="leading-snug">{q}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

export const AskTheCountyUI: React.FC<Props> = ({ tool }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const state = (location.state ?? {}) as LocationState;
  const { currentWorkspace } = useWorkspaces();

  // Priority: (1) URL ?workspace= param, (2) navigation state, (3) global context
  const urlWorkspaceId = searchParams.get('workspace') ?? undefined;
  const [localWorkspaceId, setLocalWorkspaceId] = useState<string | undefined>(
    urlWorkspaceId ??
      state.preselectedWorkspaceId ??
      currentWorkspace?.id ??
      undefined
  );

  // Sync URL param → local state when navigating directly with ?workspace=
  React.useEffect(() => {
    if (urlWorkspaceId && urlWorkspaceId !== localWorkspaceId) {
      setLocalWorkspaceId(urlWorkspaceId);
    }
  }, [urlWorkspaceId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Follow global workspace selector changes (e.g. top nav dropdown) ONLY
  // when no explicit ?workspace= URL param is present. The top-nav selector
  // updates the URL itself (WorkspaceSelector rewrites ?workspace=), so the
  // URL-sync effect below handles that path — an explicit URL param must win
  // over the (possibly stale) global currentWorkspace, otherwise navigating
  // here with ?workspace= (e.g. from a workspace's "Ask A Question" button)
  // is immediately clobbered by a previously selected workspace.
  React.useEffect(() => {
    if (urlWorkspaceId) return;
    if (currentWorkspace?.id && currentWorkspace.id !== localWorkspaceId) {
      setLocalWorkspaceId(currentWorkspace.id);
    }
  }, [currentWorkspace?.id, urlWorkspaceId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep URL param in sync with active workspace
  React.useEffect(() => {
    if (
      localWorkspaceId &&
      localWorkspaceId !== searchParams.get('workspace')
    ) {
      setSearchParams({ workspace: localWorkspaceId }, { replace: true });
    }
  }, [localWorkspaceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const workspaceId = localWorkspaceId;

  // Fetch workspace name for header label + panel via the lightweight endpoint
  const { data: panelData } = useWorkspacePanel(workspaceId);
  const workspaceName = (panelData?.workspace?.name ??
    currentWorkspace?.name) as string | undefined;

  // Trigger a question from the context panel into the chat
  const [triggerQuestion, setTriggerQuestion] = useState<
    { text: string; seq: number } | undefined
  >(
    state.prefillQuestion ? { text: state.prefillQuestion, seq: 1 } : undefined
  );
  const handleAsk = (q: string) =>
    setTriggerQuestion((prev) => ({ text: q, seq: (prev?.seq ?? 0) + 1 }));

  // Live follow-up questions generated by the LLM after each response
  const [followUpQuestions, setFollowUpQuestions] = useState<string[]>([]);

  return (
    <div className="flex flex-col gap-4">
      {/* ── Header ─────────────────────────────────────────────────── */}
      <div>
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 mb-2">
          <button
            type="button"
            onClick={() => navigate('/tools')}
            className="text-sm font-medium text-gray-900 hover:text-gray-600 transition-colors"
          >
            Tools
          </button>
          {workspaceId && (
            <>
              <span className="text-gray-900 text-sm">/</span>
              <button
                type="button"
                onClick={() => navigate(`/workspaces/${workspaceId}`)}
                className="text-sm font-medium text-gray-900 hover:text-gray-600 transition-colors"
              >
                {workspaceName ?? 'Workspace'}
              </button>
            </>
          )}
        </div>

        {/* Page title */}
        <h1 className="text-3xl font-bold text-gray-900 flex items-baseline gap-2 flex-wrap">
          On the Record
          {workspaceName && (
            <span className="text-xl font-semibold text-gray-900">
              ({workspaceName})
            </span>
          )}
        </h1>
        {tool.description && (
          <p className="mt-1 text-gray-900">{tool.description}</p>
        )}
      </div>

      {/* ── Two-column body ────────────────────────────────────────── */}
      <div
        className={
          workspaceId
            ? 'grid grid-cols-1 xl:grid-cols-[1fr_260px] gap-4 items-start'
            : ''
        }
      >
        {/* Chat column */}
        <ChatToolUI
          tool={tool}
          showModelPicker={false}
          placeholder="Ask a question about county documents…"
          workspaceId={workspaceId}
          triggerQuestion={triggerQuestion}
          onFollowUpQuestions={setFollowUpQuestions}
        />

        {/* Context panel — only when a workspace is in scope */}
        {workspaceId && (
          <div className="hidden xl:block h-[calc(100vh-220px)] min-h-[480px]">
            <WorkspaceContextPanel
              workspaceId={workspaceId}
              onAsk={handleAsk}
              followUpQuestions={followUpQuestions}
            />
          </div>
        )}
      </div>
    </div>
  );
};
